import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import {
  type ActionInputType,
  type SchemaVariant,
  getSchemaEnumValues,
  getSchemaProps,
  getSchemaRequired,
  isPlainObject,
  normalizeSchemaType,
} from './workflow-schema-utils';

export const INPUT_REF_REGEX = /\{\{\s*inputs\.([^}]+)\}\}/g;
export const STEP_OUTPUT_REF_REGEX =
  /\{\{\s*steps\.([^.}]+)\.outputs\.([^}]+)\}\}/g;
export const INPUT_REF_EXACT_REGEX = /^\{\{\s*inputs\.([^}]+)\}\}$/;
export const STEP_OUTPUT_REF_EXACT_REGEX =
  /^\{\{\s*steps\.([^.}]+)\.outputs\.([^}]+)\}\}$/;

export type InputMappingMode =
  | 'literal'
  | 'workflow-input'
  | 'step-output'
  | 'interpolated';

export const inferMappingMode = (value: unknown): InputMappingMode => {
  if (typeof value !== 'string') return 'literal';
  if (INPUT_REF_EXACT_REGEX.test(value)) return 'workflow-input';
  if (STEP_OUTPUT_REF_EXACT_REGEX.test(value)) return 'step-output';

  const hasAnyTemplate =
    INPUT_REF_REGEX.test(value) || STEP_OUTPUT_REF_REGEX.test(value);
  INPUT_REF_REGEX.lastIndex = 0;
  STEP_OUTPUT_REF_REGEX.lastIndex = 0;
  return hasAnyTemplate ? 'interpolated' : 'literal';
};

export const parseWorkflowInputTemplate = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const match = INPUT_REF_EXACT_REGEX.exec(value);
  return nonEmptyString(match?.[1]?.trim()) ?? null;
};

export const parseStepOutputTemplate = (
  value: unknown
): { stepId: string; key: string } | null => {
  if (typeof value !== 'string') return null;
  const match = STEP_OUTPUT_REF_EXACT_REGEX.exec(value);
  if (!match) return null;
  return {
    stepId: match[1].trim(),
    key: match[2].trim(),
  };
};

export const escapeTemplates = (text: string): string => {
  return text.replace(
    /(^|[^\\])({{[-\w.[\]]+}})/g,
    (_match, prefix, template) => `${prefix}\\${template}`
  );
};

export const unescapeTemplates = (text: string): string => {
  return text.replace(/\\({{[-\w.[\]]+}})/g, '$1');
};

export const isMultilineStringField = (key: string): boolean => {
  const lowered = key.toLowerCase();
  if (lowered.includes('prompt')) return true;
  if (lowered.includes('message')) return true;
  if (lowered.includes('instructions')) return true;
  if (lowered.includes('content')) return true;
  return false;
};

/** Action Input Field Def contract. */
export interface ActionInputFieldDef {
  /** Name of the action input field. */
  key: string;
  /** Whether this input must be supplied. */
  required: boolean;
  /** Description of the input field. */
  description?: string;
  /** Expected literal type of the input field. */
  type: ActionInputType;
  /** Allowed values when the input is an enumeration. */
  enumValues?: (string | number)[];
  /** Original schema describing the input field. */
  schema?: Record<string, unknown>;
}

export const buildFields = (
  effectiveInputSchema: Record<string, unknown> | undefined,
  activeVariant: SchemaVariant | undefined
): ActionInputFieldDef[] => {
  if (!effectiveInputSchema) return [];
  const props = getSchemaProps(effectiveInputSchema);
  const requiredSet = activeVariant
    ? activeVariant.required
    : getSchemaRequired(effectiveInputSchema);

  return Object.entries(props)
    .map(([key, value]) => {
      const schemaObj = isPlainObject(value) ? value : undefined;
      const enumValues = schemaObj ? getSchemaEnumValues(schemaObj) : undefined;

      const normalizedType = normalizeSchemaType(schemaObj);
      const inferredTypeFromEnum =
        normalizedType === 'unknown' && enumValues
          ? enumValues.every((v) => typeof v === 'string')
            ? ('string' as const)
            : enumValues.every((v) => typeof v === 'number')
              ? ('number' as const)
              : ('unknown' as const)
          : normalizedType;

      return {
        key,
        required: requiredSet.has(key),
        description:
          schemaObj && typeof schemaObj.description === 'string'
            ? schemaObj.description
            : undefined,
        type: inferredTypeFromEnum,
        enumValues,
        schema: schemaObj,
      };
    })
    .sort((a, b) => a.key.localeCompare(b.key));
};

export const inferModeFromInputs = (
  fields: ActionInputFieldDef[],
  inputs: Record<string, unknown>
) => {
  const modes: Record<string, InputMappingMode> = {};
  for (const field of fields) {
    modes[field.key] = inferMappingMode(inputs[field.key]);
  }
  return { modes };
};
